import { createRandomString } from 'lib/string';

import {
  ChartData,
  ChartMetaInfo,
  ChartTemplate,
  ChartTemplateContent,
  IExternalSaveLoadAdapter,
  LineToolsAndGroupsLoadRequestContext,
  LineToolsAndGroupsLoadRequestType,
  LineToolsAndGroupsState,
} from '../external/charting_library';

const VERSION = 3;

// TODO: rename but also wipe old
const CHARTS_META = `katana_charts_meta_v${VERSION}`;
const CHARTS_CONTENT = `katana_charts_content_v${VERSION}`;
const CHARTS_STUDIES = `katana_chart_studies_v${VERSION}`;
const CHARTS_DRAWINGS = `katana_chart_drawings_v${VERSION}`;
const CHARTS_TEMPLATES = `katana_chart_templates_v${VERSION}`;
// const TOOLT_AND_GROUPS = `idex_chart_templates_v${VERSION}`;

// const CHARTS_STUDIES_META = 'idex_chart_studies_meta';
// const CHARTS_STUDIES_CONTENT = 'idex_chart_studies_content';

const OLD_VERSION = 2;
let OLD_LOCAL_STORAGE_IDS = [
  `katanap_charts_meta_v${OLD_VERSION}`,
  `katanap_charts_content_v${OLD_VERSION}`,
  `katanap_chart_studies_v${OLD_VERSION}`,
  `katanap_chart_drawings_v${OLD_VERSION}`,
  `katanap_chart_templates_v${OLD_VERSION}`,
];

const loadState = (id: string): any | undefined => {
  // Wipe previous one from storage (Note: settings can be large)
  OLD_LOCAL_STORAGE_IDS.forEach(id => localStorage.removeItem(id));
  OLD_LOCAL_STORAGE_IDS = [];

  try {
    const content = localStorage.getItem(id);
    if (!content) {
      return;
    }
    return JSON.parse(content);
  } catch (error) {
    console.warn(error);
  }
};

const saveState = (id: string, state: unknown) => {
  localStorage.setItem(id, JSON.stringify(state));
};

// In memory state //
let chartsMeta: ChartMetaInfo[];
let studyTemplates: any[];
let drawingTemplates: any[];
let chartTemplates: { name: string; content: ChartTemplateContent }[];
// let toolsAndGroups: {
//   layoutId: string;
//   chartId: string;
//   state: LineToolsAndGroupsState;
// }[];

const initializeChartsMeta = (): ChartMetaInfo[] => {
  if (!chartsMeta) {
    chartsMeta = loadState(CHARTS_META) || [];
  }
  return chartsMeta;
};

const initializeStudyTemplates = () => {
  if (!studyTemplates) {
    studyTemplates = loadState(CHARTS_STUDIES) || [];
  }
};

const initializeDrawingTemplates = () => {
  if (!drawingTemplates) {
    drawingTemplates = loadState(CHARTS_DRAWINGS) || [];
  }
};

const initializeChartTemplates = () => {
  if (!chartTemplates) {
    chartTemplates = loadState(CHARTS_TEMPLATES) || [];
  }
};

// const initializeToolsAndGroups = () => {
//   if (!toolsAndGroups) {
//     toolsAndGroups = loadState(TOOLT_AND_GROUPS) || [];
//   }
// };

const addOrUpdateChart = (chartData: ChartData | any) => {
  let isNew = false;
  if (!chartData.id) {
    isNew = true;
    chartData.id = 'cID_' + createRandomString();
  }
  chartData.timestamp = new Date().valueOf();

  const chartMeta = { ...chartData };
  const chartContent = chartMeta.content;
  delete chartMeta.content;

  if (isNew) {
    chartsMeta.push(chartMeta);
  } else {
    // Update existing
    chartsMeta = chartsMeta.map(meta =>
      meta.id === chartMeta.id ? chartMeta : meta
    );
  }
  saveState(CHARTS_META, chartsMeta);
  saveState(`${CHARTS_CONTENT}_${chartMeta.id}`, chartContent);
  return Promise.resolve(chartData.id);
};

const removeChart = (id: string | number) => {
  chartsMeta = chartsMeta.filter(meta => String(meta.id) !== String(id));
  saveState(CHARTS_META, chartsMeta);
  window.localStorage.removeItem(`${CHARTS_CONTENT}_${id}`);
  return Promise.resolve();
};

// /** Note: Settings are still in memory */
export const resetSavedCharts = () => {
  window.localStorage.removeItem(CHARTS_STUDIES);
  window.localStorage.removeItem(CHARTS_DRAWINGS);
  window.localStorage.removeItem(CHARTS_META);
  window.localStorage.removeItem(CHARTS_TEMPLATES);
  // window.localStorage.removeItem(TOOLT_AND_GROUPS);
  // TO-DO: And all by ids... from CHARTS_CONTENT
};

/** https://www.tradingview.com/charting-library-docs/latest/saving_loading/ */
export const saveLoadAdapter: IExternalSaveLoadAdapter = {
  getAllCharts: function () {
    const charts = initializeChartsMeta();
    console.log('[ChartAdapter] getting', charts);
    return Promise.resolve(charts);
  },
  removeChart: function (id) {
    console.log('[ChartAdapter] removing', id);
    return removeChart(id);
  },
  saveChart: function (chartData) {
    console.log('[ChartAdapter] storing', chartData?.id);
    initializeChartsMeta();
    return addOrUpdateChart(chartData);
  },
  getChartContent: function (id) {
    console.log('[ChartAdapter] get content', id);
    const content = loadState(`${CHARTS_CONTENT}_${id}`);
    if (!content) {
      return Promise.reject('[ChartAdapter] Cannot find chart content');
    }
    return Promise.resolve(content);
  },
  removeStudyTemplate: function (studyTemplateData) {
    for (let i = 0; i < studyTemplates.length; ++i) {
      if (studyTemplates[i].name === studyTemplateData.name) {
        studyTemplates.splice(i, 1);
        return Promise.resolve();
      }
    }
    return Promise.reject();
  },
  getStudyTemplateContent: function (studyTemplateData) {
    initializeStudyTemplates();
    for (const template of studyTemplates) {
      if (template.name === studyTemplateData.name) {
        return Promise.resolve(template);
      }
    }
    console.error('[ChartAdapter] study template error');
    return Promise.reject();
  },
  saveStudyTemplate: function (studyTemplateData) {
    console.log('[ChartAdapter] Save template');
    initializeStudyTemplates();
    for (let i = 0; i < studyTemplates.length; ++i) {
      if (studyTemplates[i].name === studyTemplateData.name) {
        studyTemplates.splice(i, 1);
        break;
      }
    }
    studyTemplates.push(studyTemplateData);
    saveState(CHARTS_STUDIES, studyTemplates);
    return Promise.resolve();
  },
  getAllStudyTemplates: function () {
    initializeStudyTemplates();
    return Promise.resolve(studyTemplates);
  },
  removeDrawingTemplate: function (toolName, templateName) {
    initializeDrawingTemplates();
    for (let i = 0; i < drawingTemplates.length; ++i) {
      if (drawingTemplates[i].name === templateName) {
        drawingTemplates.splice(i, 1);
        return Promise.resolve();
      }
    }
    return Promise.reject();
  },
  loadDrawingTemplate: function (toolName, templateName) {
    initializeDrawingTemplates();
    for (const template of drawingTemplates) {
      if (template.name === templateName) {
        return Promise.resolve(template.content);
      }
    }
    console.error('[ChartAdapter] drawing: error');
    return Promise.reject();
  },
  // How it works https://www.youtube.com/watch?v=-QmnHZiR_l4
  saveDrawingTemplate: function (toolName, templateName, content) {
    console.log('[ChartAdapter] Save drawing');
    initializeDrawingTemplates();
    for (let i = 0; i < drawingTemplates.length; ++i) {
      if (drawingTemplates[i].name === templateName) {
        drawingTemplates.splice(i, 1);
        break;
      }
    }
    drawingTemplates.push({ name: templateName, content: content });
    saveState(CHARTS_DRAWINGS, drawingTemplates);
    return Promise.resolve();
  },
  getDrawingTemplates: function () {
    return Promise.resolve(
      drawingTemplates.map(function (template) {
        return template.name;
      })
    );
  },
  getChartTemplateContent: async function (
    templateName: string
  ): Promise<ChartTemplate> {
    initializeChartTemplates();
    const template = chartTemplates.find(
      template => template.name === templateName
    );
    return { content: template?.content };
  },
  getAllChartTemplates: async function (): Promise<string[]> {
    initializeChartTemplates();
    return chartTemplates.map(template => template.name);
  },
  saveChartTemplate: async function (
    newName: string,
    themeContent: ChartTemplateContent
  ): Promise<void> {
    initializeChartTemplates();
    const existing = chartTemplates.find(template => template.name === newName);
    if (existing) {
      existing.content = themeContent;
    } else {
      chartTemplates.push({ name: newName, content: themeContent });
    }
    saveState(CHARTS_TEMPLATES, chartTemplates);
  },
  removeChartTemplate: async function (templateName: string): Promise<void> {
    initializeChartTemplates();
    const updatedTemplates = chartTemplates.filter(
      template => template.name !== templateName
    );
    saveState(CHARTS_TEMPLATES, updatedTemplates);
  },
  saveLineToolsAndGroups: function (
    _layoutId: string | undefined,
    _chartId: string,
    _state: LineToolsAndGroupsState
  ) {
    // Unexpected as it's disabled by saveload_separate_drawings_storage=false
    throw new Error('[TradingView] Not implemented saving of tools');
    // const updatedState = '';
    // saveState(TOOLT_AND_GROUPS, updatedState)
  },
  loadLineToolsAndGroups: function (
    _layoutId: string | undefined,
    _chartId: string,
    _requestType: LineToolsAndGroupsLoadRequestType,
    _requestContext: LineToolsAndGroupsLoadRequestContext
  ) {
    // Unexpected as it's disabled by saveload_separate_drawings_storage=false
    throw new Error('[TradingView] Not implemented loading of tools');
    // initializeToolsAndGroups();
    // toolsAndGroups.find();
  },
};
